import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaPlay } from 'react-icons/fa';
import api from '../../services/api';

const HomeworkSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/homework/submissions/all');
      if (res.data.success) {
        setSubmissions(res.data.data);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (id, status) => {
    const feedback = prompt('Provide feedback for the student:');
    if (feedback === null) return; // cancelled

    try {
      const res = await api.put(`/homework/submissions/${id}/review`, { status, feedback });
      if (res.data.success) {
        setSubmissions(submissions.map(sub => sub._id === id ? { ...sub, status, feedback } : sub));
        alert(`Submission marked as ${status}`);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to review submission');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">Approved</span>;
      case 'rejected': return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-semibold">Rejected</span>;
      default: return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">Pending</span>;
    }
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Homework Submissions</h2>
        <p className="text-gray-600 text-sm mt-1">Review student video assignments</p>
      </div>

      {submissions.length === 0 ? (
        <p className="text-gray-500 text-center py-4">No submissions to review yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {submissions.map(sub => (
            <div key={sub._id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 relative group">
                <video
                  className="w-full h-48 object-cover"
                  controls
                  preload="metadata"
                >
                  <source src={sub.videoUrl} type="video/mp4" />
                </video>
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-gray-900 line-clamp-1">{sub.homeworkId?.title || 'Unknown Assignment'}</h3>
                  {getStatusBadge(sub.status)}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">Student: {sub.studentId?.name || 'Unknown'}</p>

                {sub.status === 'pending' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleReview(sub._id, 'approved')}
                      className="flex-1 bg-green-600 text-white py-2 rounded-md font-medium text-sm flex justify-center items-center hover:bg-green-700"
                    >
                      <FaCheck className="mr-2" /> Approve
                    </button>
                    <button
                      onClick={() => handleReview(sub._id, 'rejected')}
                      className="flex-1 bg-red-600 text-white py-2 rounded-md font-medium text-sm flex justify-center items-center hover:bg-red-700"
                    >
                      <FaTimes className="mr-2" /> Reject
                    </button>
                  </div>
                )}

                {sub.feedback && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                    <strong>Feedback:</strong> {sub.feedback}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeworkSubmissions;
